import { useCallback, useImperativeHandle, useRef, useState } from 'react'
import 'ag-grid-enterprise'
import {
  FilterChangedEvent,
  IDatasource,
  IGetRowsParams,
  ModelUpdatedEvent,
  SortChangedEvent,
} from 'ag-grid-enterprise'
import { isEqual } from 'lodash'
import { useTranslation } from 'react-i18next'
import { IdeyaAgGridTable } from '@/common/ag-grid/ideya-ag-grid-table.tsx'
import {
  DEFAULT_NO_ROWS_TEMPLATE,
  useGridAutoRefresh,
  useGridShared,
} from './hooks/use-grid-setting.ts'
import type { GridRequestContext } from './hooks/use-grid-setting.ts'
import type { InfiniteGridProps } from './grid.types.ts'

type RefreshIntent = 'refresh' | 'reload'

interface InfiniteRefreshRun {
  epoch: number
  intent: RefreshIntent
  failed: boolean
  refreshApplied: boolean
  requestStarted: boolean
  blockIds: Set<string>
}

interface InfiniteCacheBlockState {
  startRow?: number
  endRow?: number
  pageStatus?: string
}

function mergeRefreshIntent(
  current: RefreshIntent | null,
  next: RefreshIntent
): RefreshIntent {
  return current === 'reload' || next === 'reload' ? 'reload' : 'refresh'
}

export function InfiniteGrid({
  columnDefs,
  storageKey,
  fetchData,
  sortMapping,
  getContextMenuItems,
  getRowStyle,
  rowClassRules,
  autoRefreshInterval,
  onColumnEvent,
  mobileColumnOrder,
  mobileBreakpoint = 768,
  pageSize = 20,
  height,
  className,
  onRefreshed,
  tutorialColId,
  canCopy,
  canExport,
  ref,
}: InfiniteGridProps) {
  const [activeFilterColumn, setActiveFilterColumn] = useState<string>('')
  const { t } = useTranslation()

  // region Shared Grid Setting

  const {
    gridApiRef,
    isMobile,
    setIsInitialLoad,
    beginRequestEpoch,
    getCurrentRequestEpoch,
    isCurrentRequestEpoch,
    showGridLoading,
    finishGridLoading,
    showGridError,
    handleModelUpdated,
    initialState,
    resolvedColumnDefs,
    defaultColDef,
    handleGridReady,
    handleColumnChanged,
    handleColumnResized,
    handleSortChanged,
  } = useGridShared({
    columnDefs,
    storageKey,
    mobileColumnOrder,
    mobileBreakpoint,
    tutorialColId,
    onColumnEvent,
  })

  const errorMessage = t('common.error_loading_data', 'Failed to load data')
  const latestOptionsRef = useRef({
    fetchData,
    sortMapping,
    pageSize,
    onRefreshed,
    errorMessage,
  })
  latestOptionsRef.current = {
    fetchData,
    sortMapping,
    pageSize,
    onRefreshed,
    errorMessage,
  }

  const isInitialLoadRef = useRef(true)
  const pendingInitialEmptyEpochRef = useRef<number | null>(null)
  const activeRequestCountRef = useRef(0)
  const activeRefreshRunRef = useRef<InfiniteRefreshRun | null>(null)
  const queuedRefreshIntentRef = useRef<RefreshIntent | null>(null)
  const isRefreshStartScheduledRef = useRef(false)
  const startRefreshRef = useRef<(intent: RefreshIntent) => void>(
    () => undefined
  )

  const hasLoadingGridBlocks = useCallback(() => {
    const api = gridApiRef.current
    if (!api) return false

    return Object.values(
      api.getCacheBlockState() as Record<string, { pageStatus?: string }>
    ).some(({ pageStatus }) => pageStatus === 'loading')
  }, [gridApiRef])

  const startQueuedRefresh = useCallback(() => {
    if (
      isRefreshStartScheduledRef.current ||
      activeRefreshRunRef.current ||
      activeRequestCountRef.current > 0 ||
      hasLoadingGridBlocks() ||
      isInitialLoadRef.current ||
      !queuedRefreshIntentRef.current
    )
      return

    isRefreshStartScheduledRef.current = true
    queueMicrotask(() => {
      isRefreshStartScheduledRef.current = false
      const intent = queuedRefreshIntentRef.current
      queuedRefreshIntentRef.current = null
      if (intent) startRefreshRef.current(intent)
    })
  }, [hasLoadingGridBlocks])

  const finishRefresh = useCallback(() => {
    const run = activeRefreshRunRef.current
    if (
      !run ||
      (run.intent === 'reload' ? !run.requestStarted : !run.refreshApplied) ||
      activeRequestCountRef.current > 0
    )
      return

    activeRefreshRunRef.current = null

    if (queuedRefreshIntentRef.current) {
      startQueuedRefresh()
      return
    }

    if (!run.failed && isCurrentRequestEpoch(run.epoch)) {
      latestOptionsRef.current.onRefreshed?.()
    }
  }, [isCurrentRequestEpoch, startQueuedRefresh])

  const cancelRefreshNotification = useCallback(() => {
    beginRequestEpoch()
    activeRefreshRunRef.current = null
    queuedRefreshIntentRef.current = null
  }, [beginRequestEpoch])

  const evaluateRefresh = useCallback(() => {
    const api = gridApiRef.current
    const run = activeRefreshRunRef.current
    if (
      !api ||
      !run ||
      run.intent === 'reload' ||
      !isCurrentRequestEpoch(run.epoch)
    )
      return

    const blockStates = api.getCacheBlockState() as Record<
      string,
      { pageStatus?: string }
    >
    const targetStates = [...run.blockIds]
      .map((blockId) => blockStates[blockId]?.pageStatus)
      .filter((status): status is string => status !== undefined)

    if (
      targetStates.some(
        (status) => status === 'loading' || status === 'needsLoading'
      )
    )
      return

    if (targetStates.some((status) => status === 'failed')) run.failed = true
    run.refreshApplied = true
    finishRefresh()
  }, [finishRefresh, gridApiRef, isCurrentRequestEpoch])

  const startRefresh = useCallback(
    (intent: RefreshIntent) => {
      const api = gridApiRef.current
      if (!api) return

      if (
        isRefreshStartScheduledRef.current ||
        activeRefreshRunRef.current ||
        activeRequestCountRef.current > 0 ||
        hasLoadingGridBlocks() ||
        isInitialLoadRef.current
      ) {
        queuedRefreshIntentRef.current = mergeRefreshIntent(
          queuedRefreshIntentRef.current,
          intent
        )
        return
      }

      const blockStates = api.getCacheBlockState() as Record<string, unknown>
      const blockIds = Object.keys(blockStates)
      if (intent === 'refresh' && blockIds.length === 0) return

      const epoch = beginRequestEpoch()
      activeRefreshRunRef.current = {
        epoch,
        intent,
        failed: false,
        refreshApplied: false,
        requestStarted: false,
        blockIds: new Set(blockIds),
      }
      if (intent === 'reload') {
        api.purgeInfiniteCache()
      } else {
        api.refreshInfiniteCache()
      }
    },
    [beginRequestEpoch, gridApiRef, hasLoadingGridBlocks]
  )
  startRefreshRef.current = startRefresh

  const refresh = useCallback(() => startRefresh('refresh'), [startRefresh])
  const reload = useCallback(() => startRefresh('reload'), [startRefresh])

  const settleInitialLoadFromCache = useCallback(() => {
    const api = gridApiRef.current
    if (!api || !isInitialLoadRef.current) return

    const firstBlock = Object.values(
      api.getCacheBlockState() as Record<
        string,
        { startRow?: number; pageStatus?: string }
      >
    ).find(({ startRow }) => startRow === 0)

    if (!firstBlock) return

    const epoch = getCurrentRequestEpoch()
    if (firstBlock.pageStatus === 'failed') {
      showGridError(latestOptionsRef.current.errorMessage, epoch)
    } else if (firstBlock.pageStatus === 'loaded') {
      finishGridLoading(api.getDisplayedRowCount() === 0, epoch)
    } else {
      return
    }

    isInitialLoadRef.current = false
    setIsInitialLoad(false)
  }, [
    finishGridLoading,
    getCurrentRequestEpoch,
    gridApiRef,
    setIsInitialLoad,
    showGridError,
  ])

  // endregion

  // region Datasource

  const isCurrentGridRequest = useCallback(
    (params: IGetRowsParams) => {
      const api = gridApiRef.current
      if (!api) return false

      const hasLoadingRequestBlock = Object.values(
        api.getCacheBlockState() as Record<string, InfiniteCacheBlockState>
      ).some(
        ({ startRow, endRow, pageStatus }) =>
          startRow === params.startRow &&
          endRow === params.endRow &&
          pageStatus === 'loading'
      )
      if (!hasLoadingRequestBlock) return false

      const currentSortModel = api
        .getColumnState()
        .filter(({ sort }) => sort === 'asc' || sort === 'desc')
        .sort((left, right) => (left.sortIndex ?? 0) - (right.sortIndex ?? 0))
        .map(({ colId, sort }) => ({ colId, sort }))

      return (
        isEqual(params.sortModel ?? [], currentSortModel) &&
        isEqual(params.filterModel ?? {}, api.getFilterModel())
      )
    },
    [gridApiRef]
  )

  const getRowsRef = useRef<((params: IGetRowsParams) => Promise<void>) | null>(
    null
  )
  getRowsRef.current = async (params: IGetRowsParams) => {
    const capturedEpoch = (params.context as GridRequestContext | undefined)
      ?.requestEpoch
    const currentEpoch = getCurrentRequestEpoch()
    if (capturedEpoch !== currentEpoch && !isCurrentGridRequest(params)) {
      params.failCallback()
      return
    }

    // Sort/filter events can update the epoch after AG Grid creates the
    // request params. A request matching the current cache/model belongs to
    // that new epoch; delayed params for an older model were rejected above.
    const requestEpoch = currentEpoch
    const refreshRun = activeRefreshRunRef.current
    if (refreshRun?.epoch === requestEpoch && refreshRun.intent === 'reload') {
      refreshRun.requestStarted = true
    }
    activeRequestCountRef.current += 1
    const {
      fetchData: currentFetchData,
      sortMapping: currentSortMapping,
      pageSize: currentPageSize,
    } = latestOptionsRef.current
    const isFirstPage = params.startRow === 0
    // Only the very first page load owns the full-table loading/error overlay.
    // Silent refreshes reuse existing rows, so they must not toggle overlays.
    const isInitialRequest = isInitialLoadRef.current
    if (isFirstPage && isInitialRequest) showGridLoading(requestEpoch)

    try {
      // Use the filter model AG Grid captured for THIS request, not React
      // state, so a request always carries the filter it was created with.
      const filterColumns = Object.keys(params.filterModel ?? {})
      const sourceFilterColumn = filterColumns[0] ?? ''
      const filterModel = sourceFilterColumn
        ? params.filterModel[sourceFilterColumn]
        : undefined
      const filterColumn = sourceFilterColumn
        ? currentSortMapping[sourceFilterColumn] || sourceFilterColumn
        : ''
      const filterText =
        typeof filterModel?.filter === 'string' ? filterModel.filter : ''

      // Translate ag-grid colIds to backend field names via sortMapping
      const translatedSortModel = (params.sortModel ?? []).map((item) => ({
        ...item,
        colId: currentSortMapping[item.colId] || item.colId,
      }))

      const result = await currentFetchData({
        startRow: params.startRow,
        pageSize: currentPageSize,
        sortModel: translatedSortModel,
        filterColumn,
        filterText,
      })

      const rows = result.rows
      const lastRow =
        result.totalCount !== undefined
          ? result.totalCount
          : rows.length < currentPageSize
            ? params.startRow + rows.length
            : undefined

      const isInitialEmptyResult =
        isFirstPage && isInitialRequest && rows.length === 0 && lastRow === 0
      if (isInitialEmptyResult) {
        pendingInitialEmptyEpochRef.current = requestEpoch
        isInitialLoadRef.current = false
        setIsInitialLoad(false)
      }

      params.successCallback(rows, lastRow)
      if (isFirstPage && isInitialRequest && !isInitialEmptyResult) {
        // When the result is completely empty (0 rows, lastRow=0), AG Grid's
        // infinite row model may not retain the cache block, so
        // settleInitialLoadFromCache (which reads block state) would miss it
        // and leave the loading overlay stuck.
        settleInitialLoadFromCache()
      }
    } catch {
      params.failCallback()
      const run = activeRefreshRunRef.current
      if (run?.epoch === requestEpoch) run.failed = true
      if (isFirstPage && isInitialRequest) settleInitialLoadFromCache()
    } finally {
      activeRequestCountRef.current -= 1
      // Completion is driven by AG Grid's cache block state, not this promise,
      // so a stale request can never complete a newer refresh.
      evaluateRefresh()
      finishRefresh()
      startQueuedRefresh()
    }
  }

  const datasourceRef = useRef<IDatasource | null>(null)
  if (!datasourceRef.current) {
    datasourceRef.current = {
      getRows: (params) => void getRowsRef.current?.(params),
    }
  }

  // endregion

  // region Grid Event Handlers

  const handleFilterChanged = useCallback(
    (event: FilterChangedEvent) => {
      if (!event.api) return

      const filterModel = event.api.getFilterModel()
      const filterColumns = Object.keys(filterModel)

      if (filterColumns.length === 0) {
        setActiveFilterColumn('')
        cancelRefreshNotification()
        return
      }

      // Allow only one filter at a time: keep the most recently changed column
      // (i.e. the one that differs from the currently active filter, falling
      // back to the last entry when nothing was previously active).
      const changedColumn =
        filterColumns.find((col) => col !== activeFilterColumn) ??
        filterColumns[filterColumns.length - 1]

      // Prune any other filters so only the changed column remains active.
      if (filterColumns.length > 1) {
        event.api.setFilterModel({
          [changedColumn]: filterModel[changedColumn],
        })
      }

      setActiveFilterColumn(changedColumn)
      cancelRefreshNotification()
    },
    [activeFilterColumn, cancelRefreshNotification]
  )

  const handleGridSortChanged = useCallback(
    (event: SortChangedEvent) => {
      cancelRefreshNotification()
      handleSortChanged(event)
    },
    [cancelRefreshNotification, handleSortChanged]
  )

  const handleGridModelUpdated = useCallback(
    (event: ModelUpdatedEvent) => {
      const pendingEmptyEpoch = pendingInitialEmptyEpochRef.current
      if (pendingEmptyEpoch !== null) {
        pendingInitialEmptyEpochRef.current = null
        requestAnimationFrame(() => {
          finishGridLoading(true, pendingEmptyEpoch)
        })
      }
      handleModelUpdated(event)
      settleInitialLoadFromCache()
      evaluateRefresh()
      startQueuedRefresh()
    },
    [
      evaluateRefresh,
      finishGridLoading,
      handleModelUpdated,
      settleInitialLoadFromCache,
      startQueuedRefresh,
    ]
  )

  // endregion

  // region Imperative Handle

  useImperativeHandle(
    ref,
    () => ({
      refresh,
      reload,
      getGridApi: () => gridApiRef.current,
    }),
    [gridApiRef, refresh, reload]
  )

  // endregion

  // region Auto-Refresh

  useGridAutoRefresh(refresh, autoRefreshInterval)

  // endregion

  return (
    <IdeyaAgGridTable
      initialState={initialState}
      columnDefs={resolvedColumnDefs}
      defaultColDef={defaultColDef}
      rowModelType={'infinite'}
      datasource={datasourceRef.current}
      cacheBlockSize={pageSize}
      onGridReady={handleGridReady}
      onColumnMoved={handleColumnChanged}
      onColumnPinned={handleColumnChanged}
      onColumnVisible={handleColumnChanged}
      onColumnResized={handleColumnResized}
      onSortChanged={handleGridSortChanged}
      getContextMenuItems={getContextMenuItems}
      onFilterChanged={handleFilterChanged}
      onModelUpdated={handleGridModelUpdated}
      getRowStyle={getRowStyle}
      rowClassRules={rowClassRules}
      canCopy={canCopy}
      canExport={canExport}
      height={isMobile ? 400 : height}
      className={className}
      overlayNoRowsTemplate={DEFAULT_NO_ROWS_TEMPLATE}
      overlayLoadingTemplate={'<span>Loading...</span>'}
    />
  )
}

InfiniteGrid.displayName = 'InfiniteGrid'
